import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { SalesOrderStatus } from 'scheduler-types';
import { apiUrl, mockAuthHeaders } from '../lib/api';

export interface OrderUpdateData {
  customerName?: string;
  promisedShipDate?: string;
  status?: SalesOrderStatus;
}

async function updateOrder(orderId: string, updates: OrderUpdateData): Promise<{ updated: boolean }> {
  const response = await fetch(apiUrl(`/api/v1/orders/${orderId}`), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...mockAuthHeaders(),
    },
    credentials: 'include',
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      error: { message: response.statusText },
    }));
    throw new Error(error.error?.message ?? `Failed to update order: ${response.status}`);
  }

  const json = (await response.json()) as { data?: { updated: boolean } };
  return json.data ?? { updated: false };
}

async function deleteOrder(orderId: string): Promise<{ deleted: boolean }> {
  const response = await fetch(apiUrl(`/api/v1/orders/${orderId}`), {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      ...mockAuthHeaders(),
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      error: { message: response.statusText },
    }));
    throw new Error(error.error?.message ?? `Failed to delete order: ${response.status}`);
  }

  const json = (await response.json()) as { data?: { deleted: boolean } };
  return json.data ?? { deleted: false };
}

export function useUpdateOrder(orderId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (updates: OrderUpdateData) => updateOrder(orderId, updates),
    onSuccess: () => {
      // Invalidate the specific order and the orders list
      queryClient.invalidateQueries({ queryKey: ['order', orderId] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

export function useDeleteOrder(orderId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => deleteOrder(orderId),
    onSuccess: () => {
      // Invalidate the orders list
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

// --- Create Order ---

export interface CreateOrderLineItem {
  partNumber: string;
  quantity: number;
  promisedShipDate: string; // YYYY-MM-DD
}

export interface CreateOrderData {
  gpSalesOrderNumber: string;
  customerId: string;
  customerName: string;
  lineItems: CreateOrderLineItem[];
}

interface CreateOrderResponse {
  id: string;
  gpSalesOrderNumber: string;
  status: string;
}

async function createOrder(data: CreateOrderData): Promise<CreateOrderResponse> {
  const response = await fetch(apiUrl('/api/v1/orders'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...mockAuthHeaders(),
    },
    credentials: 'include',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      error: { message: response.statusText },
    }));
    throw new Error(error.error?.message ?? `Failed to create order: ${response.status}`);
  }

  const json = (await response.json()) as { data: CreateOrderResponse };
  return json.data;
}

export function useCreateOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createOrder,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}
